import {
  APIError,
  encodeURLString,
  ErrorHandling,
  EVENT_EDITOR_DATA,
  EVENT_REQUEST_EDITOR_DATA,
  executeWithRetry,
  FatalErrors,
  FeatureSwitchManager,
  fetchWrapper,
  findIsland,
  iFrameLauncherEvent,
  type iFrameLauncherMessage,
  LauncherEventType,
  Logger,
  mocksResponse,
  StudentManager,
  TeacherManager,
  UserManager,
} from "@classdojo/monsterverse-utils";
import { DEFAULT_LOCALE, initializeTranslations, isRTLLocale, translate } from "@monsterverse/i18n";
import type { SessionEntityType, TicketResponse, UserAuthType } from "@monsterverse/island-types";

import { postToApp } from "./MessageHandler";

const DEFAULT_COUNTRY_CODE = "zz"; //Not a valid ISO code, stands for unknown
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

const RANDOM_VIP_GUEST_NAME = (() => {
  const firstNames = ["Aarya", "Braden", "Owen", "Shepard", "Amayah", "Charlie", "Cora", "Rocky", "Malia", "Bria"];
  const lastNames = ["Leblanc", "Cooper", "Perez", "Shah", "Mullen", "Xiong", "Hansen", "Mendoza", "Lynch", "Terrell"];
  return {
    firstName: firstNames[Math.floor(Math.random() * (firstNames.length - 1))],
    lastName: lastNames[Math.floor(Math.random() * (lastNames.length - 1))],
  };
})();

function getEnvUrl(base: string) {
  const awsEnv = window._AWS_ENV || "local";
  if (awsEnv === "local") {
    throw new Error("Invalid AWS env");
  } else if (awsEnv === "prod") {
    return base;
  }
  return `${base}-${awsEnv}`;
}

export async function getTicket(
  groupId: string,
  entityType: SessionEntityType,
  body: string,
  opts: {
    devNameAndVersion?: string;
    editor?: string;
    guestLinkToken?: string;
    desiredPlayMode?: string;
  }
) {
  return executeWithRetry(async () => {
    const query = {
      groupId,
      entityType,
      sessionId: Logger.getSessionId(),
      ...opts,
    };

    const onlyDefined = Object.entries(query).filter(([_, value]) => value != undefined);

    const searchParamsStr = new URLSearchParams(onlyDefined).toString();

    const fetchResult = await fetchWrapper(
      "ticket",
      `https://${getEnvUrl("ticket-ex")}.multiplayer.classdojo.com/?${searchParamsStr}`,
      {
        method: "POST",
        credentials: "include",
        body,
      }
    );

    if (!fetchResult.ok) {
      try {
        const json = await fetchResult.json();
        if (json && json.method && json.url && json.status && json.statusText) {
          Logger.logWarn("API Error", {
            method: json.method,
            url: json.url,
            status: json.status,
            statusText: json.statusText,
          });

          if (fetchResult.status === HTTP_UNAUTHORIZED) {
            await setupTranslations();
            ErrorHandling.instance?.triggerFatalError(FatalErrors.UserLoggedOut);
            throw new APIError(`Error in callApi: ${json.status}`, json.method, json, json.statusText);
          } else if (fetchResult.status === HTTP_FORBIDDEN) {
            await setupTranslations();
            ErrorHandling.instance?.triggerFatalError(FatalErrors.UserLoggedOut);
            throw new APIError(
              `Error in callApi: ${json.status}`,
              json.method,
              json,
              `lockedReason=[${json.lockedReason || "unknown"}]`
            );
          } else {
            throw new APIError(`Error in callApi: ${json.status}`, json.method, json, json.statusText);
          }
        }
      } catch (e) {
        if (APIError.instanceof(e)) {
          throw e;
        }
      }
      // fallback to throw a generic error if request is not ok
      throw new Error("Failed to get ticket");
    }
    return (await fetchResult.json()) as TicketResponse;
  }, 2);
}

function isEditorBuild() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const editor = urlSearchParams.get("editor");
  return (window._AWS_ENV === "dev" || window._AWS_ENV === "local") && editor;
}

function getNameAndVersionFromURL() {
  const env = window._ENV || "local";
  if (env === "local" && !window._NGROK_HOST) {
    throw new Error("Local environment should not be checking the url subpath");
  }

  const match = new RegExp(/\/.*\/(.*-.*-.*-.*-.*)\//g).exec(window.location.pathname);
  if (match && match.length > 1) {
    return match[1];
  }
  throw new Error(`Unable to find version in url ${window.location.pathname}`);
}

function getDevNameAndVersion() {
  let version = getNameAndVersionFromURL();
  if (isEditorBuild()) {
    version = version.replace("dev", "editor");
  }
  return version;
}

async function getEditorOverrides() {
  return await new Promise<{ featureSwitches: Record<string, string> }>((resolve) => {
    const gotEditorAssets = (data: { featureSwitches: Record<string, string> }) => {
      window.removeEventListener("message", onGetEditorAssetsIFrameMessage.bind(undefined, gotEditorAssets));
      resolve(data);
    };
    window.addEventListener("message", onGetEditorAssetsIFrameMessage.bind(undefined, gotEditorAssets));
    console.log("sending request for assets");
    postToApp({ eventName: EVENT_REQUEST_EDITOR_DATA });
  });
}

function onGetEditorAssetsIFrameMessage(
  resolve: (data: { featureSwitches: Record<string, string> }) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ev: MessageEvent<any>
) {
  if (ev.data.eventName !== EVENT_EDITOR_DATA) {
    return;
  }

  resolve(ev.data.eventValues[0]);
}

async function local_getAWSHostURLs({
  entityType,
  groupID,
  bundleId,
}: {
  entityType: SessionEntityType;
  groupID: string;
  bundleId?: string;
}) {
  const guestMode = new URLSearchParams(window.location.search).get("guestMode");
  await UserManager.instance.syncUser(() =>
    UserManager.getLocalDevUser(guestMode && guestMode.length > 0 ? "guest" : entityType)
  );
  let auth: UserAuthType = "normal";
  if (guestMode === "anon") {
    auth = "guest_anon";
  } else if (guestMode === "vip") {
    auth = "guest_vip";

    // set random name for local VIP guest player here so we get
    // island members with the correct names
    const user = UserManager.instance.user;
    if (user && user.type === "student") {
      user.student.firstName = RANDOM_VIP_GUEST_NAME.firstName;
      user.student.lastName = RANDOM_VIP_GUEST_NAME.lastName;
    }
  } else if (guestMode === "visitor") {
    auth = "guest_visitor";
  } else if (guestMode === "student") {
    auth = "guest_student";
  }

  const monsterConfig =
    auth !== "normal"
      ? {
          body: "Monster_Koala_01",
          color: "Color_Red_01",
          eyes: "MonsterEyes_Duo_01_Black",
          mouth: "MonsterMouth_Med_Chip_01",
          feet: "MonsterPattern_ChestPatch_01",
          version: "5.1",
        }
      : undefined;

  const localIP = window._LOCALIP || "localhost";
  const localGroupId = groupID === "default" ? "class-abcdefg" : groupID;
  const classId = localGroupId.startsWith("class-") ? localGroupId : undefined;
  const communityId = localGroupId.startsWith("community-") ? localGroupId : undefined;
  await FeatureSwitchManager.fetchFeatureSwitches(classId, communityId);

  let editor = false;
  //If editor build, set overrides
  if (isEditorBuild()) {
    editor = true;
    const overrides = await getEditorOverrides();
    FeatureSwitchManager.featureSwitches = { ...FeatureSwitchManager.featureSwitches, ...overrides.featureSwitches };
  }

  //Setup community members
  const communityMembers = [
    auth === "guest_anon"
      ? // if playing as guest_anon, we need to add the inviter to the island members
        {
          entityId: UserManager.instance.getInviter()?.entityId,
          entityType,
          firstName: UserManager.instance.getInviter()?.firstName,
          lastName: UserManager.instance.getInviter()?.lastName,
          avatarUrl: UserManager.instance.getInviter()?.avatarUrl,
          plot: {
            islandType: "home",
            islandId: UserManager.instance.getInviter()?.entityId,
          },
        }
      : // else, we just add the current player
        {
          entityId: UserManager.instance.getEntityId(),
          entityType,
          firstName: UserManager.instance.getEntity()?.firstName,
          lastName: UserManager.instance.getEntity()?.lastName,
          avatarUrl: UserManager.instance.getEntity()?.avatarURL,
          plot: {
            islandType: "home",
            islandId: UserManager.instance.getEntityId(),
          },
        },
    // add an additional island member to test not being able to build on their plot
    {
      entityId: "521fc843178a921652000007",
      entityType: "teacher",
      firstName: "Teacher",
      lastName: "One",
      avatarUrl:
        "https://pstudent.classdojo.com/monsterWorldAvatar/eyJ2ZXJzaW9uIjoiNS4xIiwiYm9keSI6Ik1vbnN0ZXJfQW50bGVyc18wMSIsImNvbG9yIjoiQ29sb3JfUmVkXzAxIiwiZXllcyI6Ik1vbnN0ZXJFeWVzX0JlYWR5XzAxX0JsYWNrIiwibW91dGgiOiJNb25zdGVyTW91dGhfV2lkZV9TcXVhcmVfMDEiLCJmZWV0IjoiTW9uc3RlclBhdHRlcm5fVkdyYWRpZW50XzAxIiwiaSI6IjQifQ==",
      plot: {
        islandType: "home",
        islandId: "fakeOne",
      },
    },
  ];

  const wsUrl = new URL(`wss://${localIP}:8080`);
  wsUrl.searchParams.set(
    "ticket",
    encodeURLString(
      JSON.stringify({
        region: "local",
        host: "host",
        port: 1234,
        groupId: localGroupId,
        time: 0,
        entityId: UserManager.instance.getEntityId(),
        entityType: entityType === "student" ? "studentUser" : entityType,
        auth,
        inviterEntityId: UserManager.instance.getInviter()?.entityId,
      })
    )
  );
  wsUrl.searchParams.set("loggerSessionId", Logger.getSessionId());

  await fetchWrapper("dev", `https://${localIP}:8080/claimed?groupId=${localGroupId}${editor ? "&editor=true" : ""}`, {
    method: "POST",
    mode: "no-cors",
    body: JSON.stringify({
      additionalFeatureSwitches: FeatureSwitchManager.featureSwitches,
      bundleId,
      members: communityMembers,
      classYear: "1", //mock for local build
    }),
  });

  const island = {
    id: localGroupId,
    ownerName: "Local",
    ownerAvatarURL: "",
    islandName: `Local Island 🏝️`,
  };

  // Mock Class Year
  const classYear = "Local Class Year";

  return {
    wsUrl,
    editor,
    auth,
    monsterConfig,
    hubUrl: `https://${localIP}:8788`,
    island,
    countryCode: "us",
    serverRegion: "us-east-1",
    desiredServerRegion: "us-east-1",
    classYear,
  };
}

async function prod_getAWSHostURLs({
  entityType,
  groupID,
  bundleId,
}: {
  entityType: SessionEntityType;
  groupID: string;
  bundleId?: string;
}) {
  let editor = false;

  const awsEnv = window._AWS_ENV || "local";

  const ticketBody = {
    bundleId,
    featureSwitches: undefined as Record<string, string> | undefined,
  };

  //If editor build, set overrides
  if (isEditorBuild()) {
    editor = true;
    const overrides = await getEditorOverrides();
    ticketBody.featureSwitches = overrides.featureSwitches;
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  const guestLinkTokenParam = urlSearchParams.get("token");
  const guestLinkPlayModeParam = urlSearchParams.get("playMode");

  //Get the ticket
  lastTicket = await getTicket(groupID, entityType, JSON.stringify(ticketBody), {
    devNameAndVersion: awsEnv === "dev" ? getDevNameAndVersion() : undefined,
    editor: editor ? "true" : undefined,
    guestLinkToken: guestLinkTokenParam ?? undefined,
    desiredPlayMode: guestLinkPlayModeParam ?? undefined,
  });

  //Setup the hub iframe url
  let hubVersion = lastTicket.version;
  if (isEditorBuild()) {
    hubVersion = hubVersion.replace("editor", "dev");
  }
  const hubUrl = `https://clients.multiplayer.classdojo.com/hub/${hubVersion}/index.html`;

  //Setup the websocket url
  const wsUrl = new URL(`wss://${getEnvUrl("ws")}.multiplayer.classdojo.com`);
  wsUrl.searchParams.set("ticket", lastTicket.ticket);
  wsUrl.searchParams.set("loggerSessionId", Logger.getSessionId());
  wsUrl.searchParams.set("version", hubVersion);

  //Locate island from returned user data
  UserManager.instance.user = lastTicket.user;
  const auth = lastTicket.auth;

  if (lastTicket.classes) {
    if (entityType === "teacher") {
      TeacherManager.instance.classes = lastTicket.classes;
    } else {
      StudentManager.instance.classes = lastTicket.classes;
    }
  }
  FeatureSwitchManager.featureSwitches = lastTicket.featureSwitches;

  FeatureSwitchManager.customConfig = lastTicket.featureSwitchesCustomConfig;
  Logger.setEntityId(UserManager.instance.getEntityId());
  Logger.setAuthType(auth);

  const islandDetails = findIsland(lastTicket.groupId, entityType);
  if (!islandDetails) {
    throw new Error(`[getAWSHostURLs] Unable to find island details for groupId=[${lastTicket.groupId}]`);
  }
  const island = { ...islandDetails, islandName: lastTicket.islandName };
  const countryCode = lastTicket.countryCode ?? DEFAULT_COUNTRY_CODE;
  const serverRegion = lastTicket.region;
  const desiredServerRegion = lastTicket.desiredRegion;
  const monsterConfig = lastTicket.monsterConfig;
  const classYear = lastTicket.classYear;

  return {
    wsUrl,
    editor,
    auth,
    monsterConfig,
    hubUrl,
    island,
    countryCode,
    serverRegion,
    desiredServerRegion,
    classYear,
  };
}

let lastTicket: TicketResponse | undefined;
// eslint-disable-next-line sonarjs/cognitive-complexity
export async function getAWSHostURLs(groupID: string, entityType: SessionEntityType, bundleId?: string) {
  const env = window._ENV || "local";

  const {
    wsUrl,
    editor,
    auth,
    monsterConfig,
    hubUrl,
    island,
    countryCode,
    serverRegion,
    desiredServerRegion,
    classYear,
  } =
    env === "local" && !window._NGROK_HOST
      ? await local_getAWSHostURLs({
          entityType,
          groupID,
          bundleId,
        })
      : await prod_getAWSHostURLs({
          entityType,
          groupID,
          bundleId,
        });

  await setupTranslations();

  // Now that translations are setup, we can localize the name of the guest user
  if (auth === "guest_anon") {
    const user = UserManager.instance.user;
    if (user && user.type === "student") {
      if (!user.inviter?.firstName) {
        throw new Error(`[getAWSHostURLs] Guest user has no inviter, or inviter has no first name`);
      }

      user.student.firstName = translate("common.guest.name", { name: user.inviter.firstName });
    }
  } else if (auth === "guest_vip") {
    // set random name for local VIP guest player
    const user = UserManager.instance.user;
    if (user && user.type === "student") {
      user.student.firstName = RANDOM_VIP_GUEST_NAME.firstName;
      user.student.lastName = RANDOM_VIP_GUEST_NAME.lastName;
    }
  }

  return {
    wsUrl,
    hubUrl,
    island,
    //Datadog expects United Kingdom to be under "gb" instead of "uk"
    countryCode: countryCode === "uk" ? "gb" : countryCode,
    serverRegion,
    desiredServerRegion,
    editor,
    auth,
    monsterConfig,
    classYear,
  };
}

export async function setupTranslations() {
  await initializeTranslations(lastTicket?.locale || DEFAULT_LOCALE, {
    isLocal: mocksResponse(),
    build: window.build,
    logger: (level, message) => {
      switch (level) {
        case "error":
          console.error(message);
          Logger.logError(message);
          break;
        case "warn":
          console.warn(message);
          Logger.logWarn(message);
          break;
        case "info":
          console.log(message);
          Logger.logInfo(message);
          break;
      }
    },
  });

  if (isRTLLocale()) {
    // add special marker class to document body element so HTML elements
    // with class `supportsRtl` can opt-in to get rendered with `direction: rtl`
    window.document.body.classList.add("rtl");
  }
}

let iframeEl: HTMLIFrameElement | undefined;
export function handleUserDataRequests(iframe: HTMLIFrameElement) {
  iframeEl = iframe;
  window.addEventListener("message", onIFrameMessage);
}

function onIFrameMessage(ev: MessageEvent<iFrameLauncherMessage>) {
  if (ev?.data?.iFrameLauncherEvent !== iFrameLauncherEvent) {
    return;
  }

  switch (ev.data.type) {
    case LauncherEventType.GetFeatureSwitches: {
      if (FeatureSwitchManager.featureSwitches === undefined) {
        Logger.logError(
          `[STARTUP] Launcher returning undefined feature switches. lastTicket ${JSON.stringify(lastTicket)}`
        );
      }
      postMessage(LauncherEventType.GetFeatureSwitches, {
        featureSwitches: FeatureSwitchManager.featureSwitches,
        customConfig: FeatureSwitchManager.customConfig,
      });
      break;
    }
    case LauncherEventType.GetUser: {
      const env = window._ENV || "local";
      if (env === "local") {
        console.log("!! LOCAL USER", UserManager.instance.user);
        postMessage(LauncherEventType.GetUser, { user: UserManager.instance.user });
      }
      break;
    }
  }

  if (lastTicket === undefined) {
    return;
  }

  switch (ev.data.type) {
    case LauncherEventType.GetUser:
      postMessage(LauncherEventType.GetUser, { user: lastTicket.user });
      break;
    case LauncherEventType.GetClasses:
      postMessage(LauncherEventType.GetClasses, { classes: lastTicket.classes });
      break;
    case LauncherEventType.GetLocale:
      postMessage(LauncherEventType.GetLocale, { locale: lastTicket.locale || DEFAULT_LOCALE });
      break;
  }
}

function postMessage(type: LauncherEventType, data: Record<string, unknown>) {
  iframeEl?.contentWindow?.postMessage({ iFrameLauncherEvent, type, data }, "*");
}
